import { createSignal } from "solid-js";

const STORAGE_PREFIX = "flowicity_tip_dismissed_";

/**
 * Global store for managing tooltip dismissal state.
 * Uses localStorage to persist which tips have been dismissed by the user.
 */

/** Signal tracking all dismissed tooltip IDs */
const [dismissedTips, setDismissedTips] = createSignal<Set<string>>(new Set());

/**
 * Initialize the store by loading dismissed tips from localStorage.
 * Call this once at app startup.
 */
function init(): void {
  const dismissed = new Set<string>();

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) {
      const tipId = key.slice(STORAGE_PREFIX.length);
      if (localStorage.getItem(key) === "true") {
        dismissed.add(tipId);
      }
    }
  }

  setDismissedTips(dismissed);
}

/**
 * Check if a tooltip has been dismissed.
 * @param id - Unique tooltip identifier
 * @returns true if the user has dismissed this tooltip
 */
function isDismissed(id: string): boolean {
  return dismissedTips().has(id);
}

/**
 * Mark a tooltip as dismissed and persist to localStorage.
 * @param id - Unique tooltip identifier
 */
function dismiss(id: string): void {
  localStorage.setItem(STORAGE_PREFIX + id, "true");
  const updated = new Set<string>(dismissedTips());
  updated.add(id);
  setDismissedTips(updated);
}

/**
 * Reset all dismissed tooltips.
 * This will cause all tooltips to show again on next first visit.
 */
function resetAll(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(STORAGE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  setDismissedTips(new Set<string>());
}

export const tooltipStore = {
  init,
  isDismissed,
  dismiss,
  resetAll,
  /** Reactive signal of all dismissed tooltip IDs */
  dismissedTips,
};
